import { useMemo } from "react";
import { Box, Typography, Paper, Checkbox, Chip, Select, MenuItem, IconButton, Button } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { CallSplit, CallMerge, Close } from "@mui/icons-material";
import type { GrowerInfo, GrowerSortOption, JobCardInfo } from "./types";
import { makeGrowerDraggableId, makeJobDraggableId } from "./draggableId.helpers";
import { PortalTooltip } from "../../../../../../helpers";

interface MobileUnassignedListProps {
	unassignedGrowers: GrowerInfo[];
	unassignedJobs: JobCardInfo[];
	growerSortOption: GrowerSortOption;
	setGrowerSortOption: (opt: GrowerSortOption) => void;
	selectedItems: Set<string>;
	clearSelection: () => void;
	handleItemClick: (draggableId: string, shiftKey: boolean) => void;
	onSelectAll?: () => void;
	onExplodeGrower?: (growerName: string) => void;
	onRegroupGrower?: (growerName: string) => void;
}

function MobileGrowerRow({
	grower,
	isSelected,
	onToggleSelect,
	onExplode,
}: {
	grower: GrowerInfo;
	isSelected: boolean;
	onToggleSelect: (draggableId: string, shiftKey: boolean) => void;
	onExplode?: (growerName: string) => void;
}) {
	const draggableId = makeGrowerDraggableId(grower.name);
	return (
		<Paper
			elevation={1}
			onClick={(e) => onToggleSelect(draggableId, e.shiftKey)}
			sx={{
				p: 1,
				mb: 0.5,
				display: "flex",
				alignItems: "center",
				bgcolor: isSelected ? "primary.50" : "background.paper",
				userSelect: "none",
				cursor: "pointer",
			}}
		>
			<Checkbox size="small" checked={isSelected} tabIndex={-1} disableRipple sx={{ p: 0, mr: 0.5 }} />
			<Typography variant="body2" noWrap sx={{ flex: 1, mr: 1 }}>
				{grower.name}
			</Typography>
			<Chip label={`${grower.jobCount} jobs`} size="small" color="primary" variant="outlined" />
			{onExplode && grower.jobCount > 1 && (
				<PortalTooltip title="Break apart into individual jobs">
					<IconButton
						size="small"
						onClick={(e) => {
							e.stopPropagation();
							onExplode(grower.name);
						}}
						sx={{ p: 0.25, ml: 0.5 }}
					>
						<CallSplit fontSize="small" />
					</IconButton>
				</PortalTooltip>
			)}
		</Paper>
	);
}

function MobileJobRow({
	job,
	isSelected,
	onToggleSelect,
}: {
	job: JobCardInfo;
	isSelected: boolean;
	onToggleSelect: (draggableId: string, shiftKey: boolean) => void;
}) {
	const draggableId = makeJobDraggableId(job.jobId);
	return (
		<Paper
			elevation={1}
			onClick={(e) => onToggleSelect(draggableId, e.shiftKey)}
			sx={{
				p: 1,
				mb: 0.5,
				display: "flex",
				alignItems: "center",
				bgcolor: isSelected ? "primary.50" : "background.paper",
				userSelect: "none",
				cursor: "pointer",
			}}
		>
			<Checkbox size="small" checked={isSelected} tabIndex={-1} disableRipple sx={{ p: 0, mr: 0.5 }} />
			<Typography variant="body2" noWrap sx={{ flex: 1, mr: 1, fontWeight: 600 }}>
				{job.fieldName || "Unnamed Field"}
			</Typography>
			<Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 100 }}>
				{job.growerName}
			</Typography>
		</Paper>
	);
}

export function MobileUnassignedList({
	unassignedGrowers,
	unassignedJobs = [],
	growerSortOption,
	setGrowerSortOption,
	selectedItems,
	clearSelection,
	handleItemClick,
	onSelectAll,
	onExplodeGrower,
	onRegroupGrower,
}: MobileUnassignedListProps) {
	// Group unassigned jobs by grower for exploded sections
	const { unassignedJobsByGrower, explodedGrowerNames } = useMemo(() => {
		const map = new Map<string, JobCardInfo[]>();
		for (const job of unassignedJobs) {
			const existing = map.get(job.growerName);
			if (existing) {
				existing.push(job);
			} else {
				map.set(job.growerName, [job]);
			}
		}
		return { unassignedJobsByGrower: map, explodedGrowerNames: Array.from(map.keys()).sort() };
	}, [unassignedJobs]);
	const totalUnassigned = unassignedGrowers.length + unassignedJobs.length;
	const allSelected = totalUnassigned > 0 && selectedItems.size >= totalUnassigned;

	return (
		<Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
			{/* Header */}
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1, flexWrap: "wrap" }}>
				<Typography variant="subtitle2" sx={{ whiteSpace: "nowrap" }}>
					Unassigned ({totalUnassigned})
				</Typography>
				{onSelectAll && totalUnassigned > 0 && (
					<Button
						size="small"
						variant="text"
						onClick={allSelected ? clearSelection : onSelectAll}
						sx={{ minWidth: 0, px: 1, fontSize: "0.75rem", textTransform: "none" }}
					>
						{allSelected ? "Deselect All" : "Select All"}
					</Button>
				)}
				<Select
					size="small"
					value={growerSortOption}
					onChange={(e: SelectChangeEvent) => setGrowerSortOption(e.target.value as GrowerSortOption)}
					sx={{
						fontSize: "0.75rem",
						height: 28,
						minWidth: 0,
						ml: "auto",
						"& .MuiSelect-select": { py: 0.25, pr: "24px !important", pl: 1 },
					}}
				>
					<MenuItem value="name-asc" sx={{ fontSize: "0.8rem" }}>
						Name A-Z
					</MenuItem>
					<MenuItem value="name-desc" sx={{ fontSize: "0.8rem" }}>
						Name Z-A
					</MenuItem>
					<MenuItem value="count-desc" sx={{ fontSize: "0.8rem" }}>
						Jobs High→Low
					</MenuItem>
					<MenuItem value="count-asc" sx={{ fontSize: "0.8rem" }}>
						Jobs Low→High
					</MenuItem>
				</Select>
				{selectedItems.size > 0 && (
					<Chip
						label={`${selectedItems.size} selected`}
						size="small"
						color="primary"
						onDelete={clearSelection}
						deleteIcon={<Close fontSize="small" />}
						sx={{ flexShrink: 0 }}
					/>
				)}
			</Box>

			{/* Scrollable list */}
			<Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
				{totalUnassigned === 0 && (
					<Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 2 }}>
						All jobs assigned
					</Typography>
				)}

				{/* Collapsed grower cards */}
				{unassignedGrowers.map((grower) => (
					<MobileGrowerRow
						key={`grower:${grower.name}`}
						grower={grower}
						isSelected={selectedItems.has(makeGrowerDraggableId(grower.name))}
						onToggleSelect={handleItemClick}
						onExplode={onExplodeGrower}
					/>
				))}

				{/* Exploded grower sections */}
				{explodedGrowerNames.map((growerName) => {
					const jobs = unassignedJobsByGrower.get(growerName) || [];
					return (
						<Box key={`exploded-${growerName}`} sx={{ mb: 1 }}>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									py: 0.5,
									px: 0.5,
									borderBottom: "1px solid",
									borderColor: "divider",
									mb: 0.5,
								}}
							>
								<Typography variant="caption" sx={{ flex: 1, fontWeight: 600 }}>
									{growerName} ({jobs.length} jobs)
								</Typography>
								{onRegroupGrower && (
									<PortalTooltip title="Re-group into grower card">
										<IconButton size="small" onClick={() => onRegroupGrower(growerName)} sx={{ p: 0.25 }}>
											<CallMerge fontSize="small" />
										</IconButton>
									</PortalTooltip>
								)}
							</Box>
							{jobs.map((job) => (
								<MobileJobRow
									key={`job:${job.jobId}`}
									job={job}
									isSelected={selectedItems.has(makeJobDraggableId(job.jobId))}
									onToggleSelect={handleItemClick}
								/>
							))}
						</Box>
					);
				})}
			</Box>
		</Box>
	);
}
